import { useState, useEffect } from 'react';
import '@/App.css';
import { MapPin, Phone, Mail, Menu, X, ChevronDown, CheckCircle, Clock, Star, Globe, MessageCircle, Search, ArrowRight } from 'lucide-react';

const WHATSAPP_LINK = 'https://wa.me/14694718016?text=Hola%2C%20quiero%20el%20an%C3%A1lisis%20MAPA%20gratis.';
const EMAIL = 'rndigital.mx@gmail.com';
const WHATSAPP_NUMBER = '+1 469 471 8016';

function App() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setMobileMenuOpen(false);
  };

  const navLinks = [
    { id: 'servicios', label: 'Servicios' },
    { id: 'paquetes', label: 'Paquetes' },
    { id: 'faq', label: 'FAQ' },
    { id: 'contacto', label: 'Contacto' },
  ];

  const services = [
    {
      icon: <Search size={32} />,
      title: 'Google Business Profile (GBP)',
      bullets: [
        'Creación o optimización completa',
        'Categoría, servicios, horarios, descripción',
        'Mensajes + botón de WhatsApp'
      ],
      color: 'var(--teal)'
    },
    {
      icon: <Star size={32} />,
      title: 'Reseñas y reputación',
      bullets: [
        'Link + QR para reseñas',
        'Respuestas profesionales (ES/EN)',
        'Plan simple para pedir reseñas'
      ],
      color: 'var(--green)'
    },
    {
      icon: <Globe size={32} />,
      title: 'Sitio web sencillo',
      bullets: [
        'Web Express (1 página) para celular',
        'Botón WhatsApp + llamada',
        'SEO básico + publicación en link gratis (Netlify)'
      ],
      color: 'var(--orange)'
    }
  ];

  const steps = [
    { number: '1', title: 'Diagnóstico', description: 'Analizo tu perfil actual y te muestro oportunidades de mejora.' },
    { number: '2', title: 'Optimización', description: 'Aplico las mejoras para que aparezcas mejor en búsquedas locales.' },
    { number: '3', title: 'Seguimiento', description: 'Monitoreo resultados y ajusto la estrategia según sea necesario.' }
  ];

  const packages = [
    {
      name: 'GBP Starter',
      price: '$1,500',
      unit: 'MXN',
      description: 'Configuración inicial',
      features: ['Configuración/optimización del Perfil de Negocio en Google', 'Áreas de servicio + botón WhatsApp', 'Checklist de fotos y mejoras rápidas'],
      featured: false
    },
    {
      name: 'GBP + Sistema de Reseñas',
      price: '$2,800',
      unit: 'MXN',
      description: 'Lo más popular para crecer',
      features: ['Todo lo del Starter', 'Link + QR para reseñas', 'Plantillas de respuestas (ES/EN) + guión para pedir reseñas'],
      featured: true
    },
    {
      name: 'Presencia Completa (GBP + Web Express)',
      price: '$4,800',
      unit: 'MXN',
      description: 'Solución integral',
      features: ['Todo lo anterior', 'Web Express (1 página) + WhatsApp/llamada', 'SEO básico + publicación en link gratis', '1 ronda de cambios incluida'],
      featured: false
    }
  ];

  const faqs = [
    {
      question: '¿Cuánto tarda?',
      answer: 'La configuración inicial toma entre 3-5 días hábiles. Si tu negocio necesita verificación con Google, puede tomar 1-2 semanas adicionales.'
    },
    {
      question: '¿Necesito sitio web?',
      answer: 'No es obligatorio. Google Business Profile funciona de manera independiente. Sin embargo, un sitio web sencillo puede mejorar tu presencia y credibilidad.'
    },
    {
      question: '¿Me garantizas estar #1?',
      answer: 'Nadie puede garantizar el primer lugar en Google. Lo que sí garantizo es aplicar las mejores prácticas para maximizar tu visibilidad en búsquedas locales.'
    },
    {
      question: '¿Cómo consigo más reseñas?',
      answer: 'Te proporciono un link y código QR personalizado, además de estrategias probadas para pedir reseñas a tus clientes satisfechos de manera natural.'
    },
    {
      question: '¿En qué zonas trabajas?',
      answer: 'Atiendo negocios en Puerto Vallarta, Mezcales, Nuevo Vallarta y Bucerías. Trabajo de forma remota, así que no importa tu ubicación exacta en estas zonas.'
    },
    {
      question: '¿Qué incluye el Web Express?',
      answer: 'Una página rápida para celular con WhatsApp/llamada, servicios, zona de servicio, preguntas frecuentes y contacto. Se publica con un link gratuito. Dominio propio es opcional.'
    }
  ];

  return (
    <div className="app-container">
      {/* Header */}
      <header className={`header ${isScrolled ? 'scrolled' : ''}`} data-testid="header">
        <div className="container">
          <div className="flex items-center justify-between h-16 md:h-20 px-4">
            {/* Logo */}
            <a href="/" className="flex items-center flex-shrink-0" data-testid="logo-link">
              <img 
                src="/logo.png" 
                alt="Riviera Nayarit Digital" 
                className="h-11 md:h-16 w-auto"
              />
            </a>

            {/* Desktop Nav */}
            <nav className="hidden md:flex items-center gap-8" data-testid="desktop-nav">
              {navLinks.map((link) => (
                <button
                  key={link.id}
                  onClick={() => scrollToSection(link.id)}
                  className="text-base font-medium hover:opacity-70 transition-opacity bg-transparent border-none cursor-pointer"
                  style={{ color: 'var(--navy)' }}
                  data-testid={`nav-${link.id}`}
                >
                  {link.label}
                </button>
              ))}
              <a
                href={WHATSAPP_LINK}
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-primary flex items-center gap-2"
                data-testid="header-whatsapp-btn"
              >
                <MessageCircle size={18} />
                <span className="hidden lg:inline">WhatsApp</span>
                <span className="lg:hidden">WA</span>
              </a>
            </nav>

            {/* Mobile Menu Button */}
            <button
              className="md:hidden p-2 bg-transparent border-none cursor-pointer"
              onClick={() => setMobileMenuOpen(true)}
              aria-label="Abrir menú"
              data-testid="mobile-menu-btn"
            >
              <Menu size={28} style={{ color: 'var(--navy)' }} />
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Menu Overlay */}
      <div 
        className={`mobile-menu-overlay ${mobileMenuOpen ? 'active' : ''}`}
        onClick={() => setMobileMenuOpen(false)}
        data-testid="mobile-menu-overlay"
      />

      {/* Mobile Menu */}
      <div className={`mobile-menu ${mobileMenuOpen ? 'active' : ''}`} data-testid="mobile-menu">
        <div className="flex justify-between items-center mb-8">
          <span className="text-lg font-bold" style={{ color: 'var(--navy)' }}>Menú</span>
          <button
            onClick={() => setMobileMenuOpen(false)}
            className="p-2 bg-transparent border-none cursor-pointer relative z-50"
            aria-label="Cerrar menú"
            data-testid="mobile-menu-close"
          >
            <X size={24} style={{ color: 'var(--navy)' }} />
          </button>
        </div>
        <nav className="flex flex-col gap-4">
          {navLinks.map((link) => (
            <button
              key={link.id}
              onClick={() => scrollToSection(link.id)}
              className="text-left text-lg font-medium py-2 bg-transparent border-none cursor-pointer"
              style={{ color: 'var(--navy)' }}
              data-testid={`mobile-nav-${link.id}`}
            >
              {link.label}
            </button>
          ))}
        </nav>
        <div className="mt-auto pt-8">
          <a
            href={WHATSAPP_LINK}
            target="_blank"
            rel="noopener noreferrer"
            className="btn btn-primary w-full justify-center"
            data-testid="mobile-whatsapp-btn"
          >
            <MessageCircle size={20} />
            WhatsApp (Análisis Gratis)
          </a>
        </div>
      </div>

      {/* Hero Section */}
      <section className="section pt-32 md:pt-40" style={{ background: 'linear-gradient(180deg, var(--light) 0%, white 100%)' }} data-testid="hero-section">
        <div className="container px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h1 
              className="text-3xl md:text-4xl lg:text-5xl font-bold leading-tight mb-6"
              style={{ color: 'var(--navy)' }}
              data-testid="hero-title"
            >
              Atrae más clientes desde{' '}
              <span className="text-gradient">Google Maps</span>
            </h1>
            <p 
              className="text-lg md:text-xl mb-8 leading-relaxed"
              style={{ color: '#4a5568' }}
              data-testid="hero-subtitle"
            >
              Optimizo tu Perfil de Negocio en Google para que recibas más llamadas y mensajes. 
              <strong> Atención bilingüe ES/EN.</strong>
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-10">
              <a
                href={WHATSAPP_LINK}
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-primary btn-large"
                data-testid="hero-whatsapp-btn"
              >
                <MessageCircle size={22} />
                WhatsApp (Análisis Gratis)
              </a>
              <button
                onClick={() => scrollToSection('servicios')}
                className="btn btn-secondary btn-large"
                data-testid="hero-services-btn"
              >
                Ver Servicios
                <ArrowRight size={20} />
              </button>
            </div>
            {/* Trust Row */}
            <div className="flex flex-wrap justify-center gap-6 md:gap-10" data-testid="trust-row">
              {[
                { icon: <MapPin size={18} />, text: 'Servicio local' },
                { icon: <Globe size={18} />, text: 'Bilingüe ES/EN' },
                { icon: <CheckCircle size={18} />, text: 'Enfoque en resultados' }
              ].map((item, idx) => (
                <div key={idx} className="flex items-center gap-2" style={{ color: 'var(--teal)' }}>
                  {item.icon}
                  <span className="text-sm font-medium" style={{ color: 'var(--navy)' }}>{item.text}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="servicios" className="section" style={{ background: 'white' }} data-testid="servicios-section">
        <div className="container px-4">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-bold mb-4" style={{ color: 'var(--navy)' }} data-testid="servicios-title">
              Servicios
            </h2>
            <p className="text-lg" style={{ color: '#4a5568' }}>Todo lo que necesitas para destacar en Google</p>
          </div>
          <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
            {services.map((service, idx) => (
              <div key={idx} className="card" data-testid={`service-card-${idx}`}>
                <div 
                  className="w-14 h-14 rounded-xl flex items-center justify-center mb-5"
                  style={{ background: `${service.color}15`, color: service.color }}
                >
                  {service.icon}
                </div>
                <h3 className="text-xl font-bold mb-4" style={{ color: 'var(--navy)' }}>{service.title}</h3>
                <ul className="space-y-3">
                  {service.bullets.map((bullet, bidx) => (
                    <li key={bidx} className="flex items-start gap-3">
                      <CheckCircle size={18} style={{ color: service.color, flexShrink: 0, marginTop: 2 }} />
                      <span style={{ color: '#4a5568' }}>{bullet}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How We Work Section */}
      <section className="section" style={{ background: 'var(--light)' }} data-testid="como-trabajamos-section">
        <div className="container px-4">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-bold mb-4" style={{ color: 'var(--navy)' }} data-testid="como-trabajamos-title">
              Cómo trabajamos
            </h2>
            <p className="text-lg" style={{ color: '#4a5568' }}>Un proceso simple y efectivo</p>
          </div>
          <div className="grid md:grid-cols-3 gap-8 relative">
            {steps.map((step, idx) => (
              <div key={idx} className="relative" data-testid={`step-${idx}`}>
                {idx < steps.length - 1 && <div className="step-connector" />}
                <div className="text-center">
                  <div 
                    className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-5 text-2xl font-bold text-white"
                    style={{ background: idx === 0 ? 'var(--teal)' : idx === 1 ? 'var(--green)' : 'var(--orange)' }}
                  >
                    {step.number}
                  </div>
                  <h3 className="text-xl font-bold mb-3" style={{ color: 'var(--navy)' }}>{step.title}</h3>
                  <p style={{ color: '#4a5568' }}>{step.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Packages Section */}
      <section id="paquetes" className="section" style={{ background: 'white' }} data-testid="paquetes-section">
        <div className="container px-4">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-bold mb-4" style={{ color: 'var(--navy)' }} data-testid="paquetes-title">
              Paquetes
            </h2>
            <p className="text-lg" style={{ color: '#4a5568' }}>Elige el plan que mejor se adapte a tu negocio</p>
          </div>
          <div className="grid md:grid-cols-3 gap-6 lg:gap-8 mb-8">
            {packages.map((pkg, idx) => (
              <div 
                key={idx} 
                className={`card pricing-card ${pkg.featured ? 'featured' : ''}`}
                data-testid={`package-card-${idx}`}
              >
                <h3 className="text-xl font-bold mb-2" style={{ color: 'var(--navy)' }}>{pkg.name}</h3>
                <p className="text-sm mb-4" style={{ color: '#4a5568' }}>{pkg.description}</p>
                <div className="mb-6">
                  <span className="text-sm" style={{ color: '#4a5568' }}>Desde </span>
                  <span className="text-3xl font-bold" style={{ color: 'var(--teal)' }}>{pkg.price}</span>
                  <span className="text-sm ml-1" style={{ color: '#4a5568' }}>{pkg.unit}</span>
                </div>
                <ul className="space-y-3 mb-6">
                  {pkg.features.map((feature, fidx) => (
                    <li key={fidx} className="flex items-center gap-3">
                      <CheckCircle size={18} style={{ color: 'var(--green)', flexShrink: 0 }} />
                      <span style={{ color: '#4a5568' }}>{feature}</span>
                    </li>
                  ))}
                </ul>
                <a
                  href={WHATSAPP_LINK}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`btn w-full justify-center ${pkg.featured ? 'btn-primary' : 'btn-secondary'}`}
                  data-testid={`package-cta-${idx}`}
                >
                  <MessageCircle size={18} />
                  Solicitar Info
                </a>
              </div>
            ))}
          </div>
          <p className="text-center text-sm" style={{ color: '#4a5568' }} data-testid="pricing-note">
            Precios de lanzamiento. Dependen del estado actual del perfil y el número de servicios.
          </p>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="section" style={{ background: 'var(--light)' }} data-testid="faq-section">
        <div className="container px-4">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-bold mb-4" style={{ color: 'var(--navy)' }} data-testid="faq-title">
              Preguntas Frecuentes
            </h2>
          </div>
          <div className="max-w-2xl mx-auto">
            {faqs.map((faq, idx) => (
              <div key={idx} className="faq-item" data-testid={`faq-item-${idx}`}>
                <button
                  className="faq-question"
                  onClick={() => setOpenFaq(openFaq === idx ? null : idx)}
                  aria-expanded={openFaq === idx}
                  data-testid={`faq-question-${idx}`}
                >
                  {faq.question}
                  <ChevronDown size={20} className={`faq-icon ${openFaq === idx ? 'open' : ''}`} />
                </button>
                <div className={`faq-answer ${openFaq === idx ? 'open' : ''}`} data-testid={`faq-answer-${idx}`}>
                  {faq.answer}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section 
        id="contacto" 
        className="section" 
        style={{ background: 'linear-gradient(135deg, var(--navy) 0%, #004080 100%)' }}
        data-testid="cta-section"
      >
        <div className="container px-4">
          <div className="max-w-2xl mx-auto text-center">
            <div 
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-6"
              style={{ background: 'rgba(255,255,255,0.1)', color: 'white' }}
            >
              <Clock size={18} />
              <span className="text-sm font-medium">10 minutos</span>
            </div>
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold mb-4 text-white" data-testid="cta-title">
              Análisis GRATIS
            </h2>
            <p className="text-lg mb-2" style={{ color: 'rgba(255,255,255,0.85)' }} data-testid="cta-subtitle">
              Te digo 3 mejoras rápidas para aparecer mejor en Google Maps.
            </p>
            <p className="text-base mb-8" style={{ color: 'rgba(255,255,255,0.7)' }}>
              ¿Quieres también tu Web Express? Te la dejo lista para recibir mensajes.
            </p>
            <a
              href={WHATSAPP_LINK}
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-large inline-flex"
              style={{ background: 'var(--green)', color: 'white' }}
              data-testid="cta-whatsapp-btn"
            >
              <MessageCircle size={24} />
              Escribir "MAPA" por WhatsApp
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer style={{ background: 'var(--navy)', color: 'white', padding: '3rem 1rem' }} data-testid="footer">
        <div className="container px-4">
          <div className="max-w-4xl mx-auto">
            <div className="grid md:grid-cols-3 gap-8 mb-8">
              {/* Brand */}
              <div>
                <div className="flex items-center gap-3 mb-4">
                  <img 
                    src="/logo.png" 
                    alt="Riviera Nayarit Digital" 
                    className="h-10 w-auto"
                  />
                </div>
                <p className="text-sm" style={{ color: 'rgba(255,255,255,0.7)' }}>
                  Tu negocio visible en Google y WhatsApp
                </p>
              </div>

              {/* Service Area */}
              <div>
                <h4 className="font-semibold mb-3 flex items-center gap-2">
                  <MapPin size={18} style={{ color: 'var(--teal)' }} />
                  Área de Servicio
                </h4>
                <p className="text-sm" style={{ color: 'rgba(255,255,255,0.7)' }} data-testid="service-area">
                  Puerto Vallarta • Mezcales • Nuevo Vallarta • Bucerías
                </p>
              </div>

              {/* Contact */}
              <div>
                <h4 className="font-semibold mb-3">Contacto</h4>
                <div className="space-y-2">
                  <a 
                    href={WHATSAPP_LINK}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-sm hover:opacity-80 transition-opacity"
                    style={{ color: 'rgba(255,255,255,0.7)' }}
                    data-testid="footer-whatsapp"
                  >
                    <Phone size={16} style={{ color: 'var(--green)' }} />
                    {WHATSAPP_NUMBER}
                  </a>
                  <a 
                    href={`mailto:${EMAIL}`}
                    className="flex items-center gap-2 text-sm hover:opacity-80 transition-opacity"
                    style={{ color: 'rgba(255,255,255,0.7)' }}
                    data-testid="footer-email"
                  >
                    <Mail size={16} style={{ color: 'var(--orange)' }} />
                    {EMAIL}
                  </a>
                </div>
              </div>
            </div>

            {/* Copyright */}
            <div 
              className="pt-6 text-center text-sm"
              style={{ borderTop: '1px solid rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.5)' }}
            >
              © {new Date().getFullYear()} Riviera Nayarit Digital. Todos los derechos reservados.
            </div>
          </div>
        </div>
      </footer>

      {/* Floating WhatsApp Button */}
      <a
        href={WHATSAPP_LINK}
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-20 right-4 md:bottom-6 md:right-6 z-50 flex items-center gap-2 px-4 py-3 md:px-5 md:py-3 rounded-full shadow-lg transition-transform hover:scale-105"
        style={{ background: '#25D366', color: 'white' }}
        data-testid="floating-whatsapp-btn"
        aria-label="Contactar por WhatsApp"
      >
        <MessageCircle size={24} fill="white" />
        <span className="hidden md:inline font-semibold">WhatsApp</span>
      </a>
    </div>
  );
}

export default App;
